import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

const icons = {
  crown: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4l3 12h14l3-12-5 4-5-4-5 4-3-4z"/><path d="M3 20h18"/>
    </svg>
  ),
  shield: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  heart: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
  users: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  star: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  arrowRight: (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
};

const galleryImages = [
  { src: '/images/gallery/storefront.jpeg', alt: 'Royal Parcel storefront at 11104 Magnolia Blvd, sign above the service window and entrance' },
  { src: '/images/gallery/window-signage.jpeg', alt: 'Storefront window listing packing, shipping, scanning, printing, laminating, shredding and notary, beside the entrance at 11104' },
  { src: '/images/gallery/mailbox-wall.jpeg', alt: 'Wall of private mailboxes with shipping box sizes displayed along the top' },
  { src: '/images/gallery/interior-wide.jpeg', alt: 'Store interior showing the mailbox wall, boxes in every size, and the mailbox pricing banner' },
  { src: '/images/gallery/service-counter.jpeg', alt: 'Service counter and mailbox wall seen from inside the store' },
  { src: '/images/gallery/counter-detail.jpeg', alt: 'Service counter with UPS and USPS shipping signage and packing supplies stocked behind' },
  { src: '/images/gallery/pricing-banner.jpeg', alt: 'In-store banner listing mailbox pricing for small, medium and large plans by agreement length' },
  { src: '/images/gallery/parking-gate.jpeg', alt: 'Royal Parcel parking gate off the rear alley, marked 11104 Magnolia Blvd' },
  { src: '/images/gallery/parking-lot.jpeg', alt: 'Private parking lot behind the store with marked spaces including accessible parking' },
  { src: '/images/gallery/parking-rear-entrance.jpeg', alt: 'Rear entrance to Royal Parcel from the parking lot, under the Packing, Shipping, Mailboxes awning' },
];

function About() {
  const [lightbox, setLightbox] = useState(null);
  const [sliderIndex, setSliderIndex] = useState(0);
  const sliderRef = useRef(null);
  const touchStartX = useRef(0);

  const slideNext = useCallback(() => {
    setSliderIndex((prev) => (prev + 1) % galleryImages.length);
  }, []);

  const slidePrev = useCallback(() => {
    setSliderIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, []);

  const nextImage = useCallback((e) => {
    e.stopPropagation();
    setLightbox((prev) => (prev + 1) % galleryImages.length);
  }, []);

  const prevImage = useCallback((e) => {
    e.stopPropagation();
    setLightbox((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, []);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) slideNext();
      else slidePrev();
    }
  }, [slideNext, slidePrev]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightbox === null) return;
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox((prev) => (prev + 1) % galleryImages.length);
      if (e.key === 'ArrowLeft') setLightbox((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox]);

  useEffect(() => {
    if (lightbox !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightbox]);

  useEffect(() => {
    document.title = 'About Royal Parcel | North Hollywood Shipping & Postal Center';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      'Learn about Royal Parcel — North Hollywood\'s trusted shipping and postal center on Magnolia Blvd. Locally owned, serving the NoHo community with premium pack & ship, private mailbox, notary, and business services.'
    );
  }, []);

  return (
    <>
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <p className="section-overline section-overline--light" style={{ justifyContent: 'center' }}>
            <span style={{ display: 'none' }}>—</span>Our Story
          </p>
          <h1>About Royal Parcel</h1>
          <p>Proudly serving the North Hollywood community with premium shipping and postal services.</p>
        </div>
      </section>

      {/* Our Story */}
      <section className="about-story">
        <div className="container">
          <div className="about-story-grid">
            <div className="about-story-image">
              <img src="/images/logo.png" alt="Royal Parcel" />
            </div>
            <div className="about-story-content">
              <p className="section-overline">Who We Are</p>
              <h2>Your Neighborhood Postal Royalty</h2>
              <p>
                Royal Parcel was founded with a simple mission: to provide the North Hollywood 
                community with a shipping and postal experience that feels truly premium. Located 
                on historic Magnolia Boulevard, we've built our reputation on reliability, 
                personalized service, and a commitment to treating every customer like royalty.
              </p>
              <p>
                Under the leadership of CEO Vik Vartan D., our team has grown into a full-service 
                postal center offering everything from private mailboxes with real street addresses 
                to professional notary services, secure shredding, and worldwide shipping through 
                major carriers.
              </p>
              <p>
                We believe that shipping and postal services shouldn't feel like a chore. That's 
                why we've created an environment where convenience meets quality — a place where 
                the world truly is yours to ship around.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="gallery-section">
        <div className="container">
          <div className="section-header" style={{ textAlign: 'center' }}>
            <p className="section-overline" style={{ justifyContent: 'center' }}>
              <span style={{ display: 'none' }}>—</span>Our Location
            </p>
            <h2 className="section-title">Inside Royal Parcel</h2>
            <p className="section-subtitle" style={{ margin: '0 auto', textAlign: 'center' }}>
              Take a look at our North Hollywood location — fully equipped to handle all your shipping, mailing, and business needs.
            </p>
          </div>

          <div className="gallery-slider-wrapper">
            <button className="gallery-nav gallery-nav--prev" onClick={slidePrev} aria-label="Previous">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>

            <div
              className="gallery-slider"
              ref={sliderRef}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="gallery-slider-track"
                style={{ transform: `translateX(-${sliderIndex * 100}%)` }}
              >
                {galleryImages.map((img, index) => (
                  <div className="gallery-slide" key={index} onClick={() => setLightbox(index)}>
                    <div
                      className="gallery-slide-bg"
                      style={{ backgroundImage: `url(${img.src})` }}
                      aria-hidden="true"
                    />
                    <img src={img.src} alt={img.alt} loading="lazy" />
                    <div className="gallery-overlay">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button className="gallery-nav gallery-nav--next" onClick={slideNext} aria-label="Next">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          <div className="gallery-dots">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                className={`gallery-dot${index === sliderIndex ? ' gallery-dot--active' : ''}`}
                onClick={() => setSliderIndex(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)} aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <button className="lightbox-nav lightbox-prev" onClick={prevImage} aria-label="Previous">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <img
            src={galleryImages[lightbox].src}
            alt={galleryImages[lightbox].alt}
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
          <button className="lightbox-nav lightbox-next" onClick={nextImage} aria-label="Next">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <div className="lightbox-counter">{lightbox + 1} / {galleryImages.length}</div>
        </div>
      )}

      {/* Values */}
      <section className="about-values">
        <div className="container">
          <div className="section-header">
            <p className="section-overline section-overline--light">Our Values</p>
            <h2 className="section-title section-title--light">What Drives Us</h2>
            <p className="section-subtitle section-subtitle--light" style={{ margin: '0 auto' }}>
              Every package we handle, every document we process, is guided by our core principles.
            </p>
          </div>

          <div className="values-grid">
            <div className="value-card">
              <div className="value-card-icon">{icons.crown}</div>
              <h3>Royal Treatment</h3>
              <p>
                Every customer receives our signature royal treatment. We take the time to understand 
                your needs and deliver service that exceeds expectations.
              </p>
            </div>
            <div className="value-card">
              <div className="value-card-icon">{icons.shield}</div>
              <h3>Trust & Security</h3>
              <p>
                Your packages and personal information are safe with us. We maintain the highest 
                standards of security for all items in our care.
              </p>
            </div>
            <div className="value-card">
              <div className="value-card-icon">{icons.heart}</div>
              <h3>Community First</h3>
              <p>
                We're proud to be part of the North Hollywood community. Supporting local businesses 
                and residents is at the heart of everything we do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <h2>Experience the Royal Difference</h2>
          <p>Come visit us and see why our customers keep coming back. We're ready to serve you.</p>
          <Link to="/contact" className="btn">
            Visit Us Today {icons.arrowRight}
          </Link>
        </div>
      </section>
    </>
  );
}

export default About;
